/**
 * @file 颜色变量的 native 版本,
 * 与 web 区别是文字颜色不透明 (因为 android 对文字颜色透明度支持不完善)
 */
import { getBrandColors } from './brand-colors';
/**
 * 背景色
 */
export const backgroundColors = {
    /**
     * 白色
     */
    _0: '#fff',
    /**
     * 玻璃材质色，表格深色行背景
     */
    _1: '#f9fafc',
    /**
     * 卡片分区内容的背景色，控件悬停色
     */
    _2: '#f6f8fa',
    /**
     * 页面背景色、表头、弹窗标题栏背景色
     */
    _3: '#f3f4f8',
    /**
     * 输入控件禁用
     */
    _4: '#f0f1f5',
    /**
     * 点按、选中
     */
    _5: '#edeff2',
    /**
     * 开关、单选框、复选框等控件禁用
     */
    _6: '#e4e6eb',
};
/**
 * 字体颜色
 */
export const getTextColors = (themeColor) => {
    const brandColors = getBrandColors(themeColor);
    const textColor = {
        /**
         * 白色
         */
        _0: '#ffffff',
        /**
         * 与主色对应的文字色
         */
        brand: brandColors.text0,
        /**
         * 标题, 主要正文
         */
        _1: '#2b303b',
        /**
         * 次要正文, 文字按钮
         */
        _2: '#5d6169',
        /**
         * 页面内的注释信息
         */
        _3: '#8f9197',
        /**
         * 控件内的占位信息，禁用状态控件的文字
         */
        _4: '#c1c2c5',
    };
    return textColor;
};
/**
 * 分割线色
 */
export const dividerColors = {
    /**
     * 深色，用于常见的内容区分
     */
    _1: 'rgba(50, 56, 66, 0.1)',
    /**
     * 浅色分割线, 用于部分控件细节
     */
    _2: 'rgba(50, 56, 66, 0.06)',
};
/**
 * 边框颜色
 */
export const borderColors = {
    /**
     * 控件边框浅色
     */
    greyLight: 'rgba(50, 56, 66, 0.1)',
    /**
     * 控件边框默认色
     */
    greyNormal: '#d3d5db',
    /**
     * 控件边框悬停点按色
     */
    greyDark: '#8f9197',
};
/**
 * 状态色
 */
export const statusColors = {
    /**
     * 成功状态色
     */
    success: {
        /**
         * 浅色背景
         */
        backgroundLight: '#e3f8f3',
        /**
         * 图形元素
         */
        element: '#16c89e',
        /**
         * 默认背景
         */
        background: '#21a18e',
        /**
         * 文字
         */
        text: '#00795c',
    },
    /**
     * 进行中状态色
     */
    ongoing: {
        /**
         * 浅色背景
         */
        backgroundLight: '#ecf4ff',
        /**
         * 图形元素
         */
        element: '#477cff',
        /**
         * 默认背景
         */
        background: '#477cff',
        /**
         * 文字
         */
        text: '#447ae6',
    },
    /**
     * 警告状态色
     */
    warning: {
        /**
         * 浅色背景
         */
        backgroundLight: '#fff5e3',
        /**
         * 图形元素
         */
        element: '#fca700',
        /**
         * 默认背景
         */
        background: '#e79900',
        /**
         * 文字
         */
        text: '#ad5b00',
    },
    /**
     * 危险状态色
     */
    danger: {
        /**
         * 浅色背景
         */
        backgroundLight: '#fdefef',
        /**
         * 图形元素
         */
        element: '#f7585b',
        /**
         * 浅色图形元素
         */
        elementLight: '#f8d4d2',
        /**
         * 默认背景
         */
        background: '#e4524b',
        /**
         * 文字
         */
        text: '#c41a1d',
    },
    /**
     * 失效状态色
     */
    invalid: {
        /**
         * 浅色背景
         */
        backgroundLight: '#e4e6eb',
        /**
         * 图形元素
         */
        element: '#8f9197',
        /**
         * 默认背景
         */
        background: '#8f9197',
        /**
         * 文字
         */
        text: 'rgba(6, 12, 25, 0.65)',
    },
};
/**
 * 辅色
 */
const UIColor = {
    _1: {
        /**
         * 背景色
         */
        background: '#477cff',
        /**
         * 浅背景色
         */
        backgroundLight: '#ecf4ff',
        /**
         * 文字颜色
         */
        text: '#447ae6',
    },
    _2: {
        /**
         * 背景色
         */
        background: '#15b8b1',
        /**
         * 浅背景色
         */
        backgroundLight: '#e1fbfa',
        /**
         * 文字颜色
         */
        text: '#1d8e89',
    },
    _3: {
        /**
         * 背景色
         */
        background: '#e79900',
        /**
         * 浅背景色
         */
        backgroundLight: '#fff5e3',
        /**
         * 文字颜色
         */
        text: '#ad5b00',
    },
    _4: {
        /**
         * 背景色
         */
        background: '#d45586',
        /**
         * 浅背景色
         */
        backgroundLight: '#ffebef',
        /**
         * 文字颜色
         */
        text: '#b5295f',
    },
    _5: {
        /**
         * 背景色
         */
        background: '#8e6bf1',
        /**
         * 浅背景色
         */
        backgroundLight: '#eeecff',
        /**
         * 文字颜色
         */
        text: '#6a4bbe',
    },
    _6: {
        /**
         * 背景色
         */
        background: '#5975ba',
        /**
         * 浅背景色
         */
        backgroundLight: '#dbe3f5',
        /**
         * 文字颜色
         */
        text: '#3f5a9b',
    },
    _7: {
        /**
         * 背景色
         */
        background: '#60c381',
        /**
         * 浅背景色
         */
        backgroundLight: '#e7f9ed',
        /**
         * 文字颜色
         */
        text: '#3e8b57',
    },
    _8: {
        /**
         * 背景色
         */
        background: '#8e6796',
        /**
         * 浅背景色
         */
        backgroundLight: '#ebe3ec',
        /**
         * 文字颜色
         */
        text: '#77577d',
    },
    _9: {
        /**
         * 背景色
         */
        background: '#ea7b25',
        /**
         * 浅背景色
         */
        backgroundLight: '#fff1e3',
        /**
         * 文字颜色
         */
        text: '#c05400',
    },
    _10: {
        /**
         * 背景色
         */
        background: '#757aa6',
        /**
         * 浅背景色
         */
        backgroundLight: '#e4e6f2',
        /**
         * 文字颜色
         */
        text: '#5c618f',
    },
    _11: {
        /**
         * 背景色
         */
        background: '#17a0de',
        /**
         * 浅背景色
         */
        backgroundLight: '#dcf4ff',
        /**
         * 文字颜色
         */
        text: '#137dad',
    },
    _12: {
        /**
         * 背景色
         */
        background: '#829b9e',
        /**
         * 浅背景色
         */
        backgroundLight: '#dae4e5',
        /**
         * 文字颜色
         */
        text: '#526c6f',
    },
    _13: {
        /**
         * 背景色
         */
        background: '#c85be0',
        /**
         * 浅背景色
         */
        backgroundLight: '#f7e9fa',
        /**
         * 文字颜色
         */
        text: '#9e37b4',
    },
    _14: {
        /**
         * 背景色
         */
        background: '#4a9995',
        /**
         * 浅背景色
         */
        backgroundLight: '#cde6e5',
        /**
         * 文字颜色
         */
        text: '#317572',
    },
};
/**
 * 配色方案
 */
export const palette = [
    UIColor._1,
    UIColor._2,
    UIColor._3,
    UIColor._4,
    UIColor._5,
    UIColor._6,
    UIColor._7,
    UIColor._8,
    UIColor._9,
    UIColor._10,
    UIColor._11,
    UIColor._12,
    UIColor._13,
    UIColor._14,
];
/**
 * 根据下标从颜色盘获取配色方案
 * 若下标超出颜色盘总数，则从头开始取
 *
 * @param index - 下标
 * @returns 配色方案
 */
export const getPaletteColorByIndex = (index) => palette[index % palette.length];
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29sb3JzLm5hdGl2ZS5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9jb2xvcnMubmF0aXZlLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOzs7R0FHRztBQUVILE9BQU8sRUFBRSxjQUFjLEVBQUUsTUFBTSxnQkFBZ0IsQ0FBQTtBQUcvQzs7R0FFRztBQUNILE1BQU0sQ0FBQyxNQUFNLGdCQUFnQixHQUFHO0lBQzlCOztPQUVHO0lBQ0gsRUFBRSxFQUFFLE1BQU07SUFDVjs7T0FFRztJQUNILEVBQUUsRUFBRSxTQUFTO0lBQ2I7O09BRUc7SUFDSCxFQUFFLEVBQUUsU0FBUztJQUNiOztPQUVHO0lBQ0gsRUFBRSxFQUFFLFNBQVM7SUFDYjs7T0FFRztJQUNILEVBQUUsRUFBRSxTQUFTO0lBQ2I7O09BRUc7SUFDSCxFQUFFLEVBQUUsU0FBUztJQUNiOztPQUVHO0lBQ0gsRUFBRSxFQUFFLFNBQVM7Q0FDTCxDQUFBO0FBRVY7O0dBRUc7QUFDSCxNQUFNLENBQUMsTUFBTSxhQUFhLEdBQUcsQ0FBQyxVQUFrQixFQUFFLEVBQUU7SUFDbEQsTUFBTSxXQUFXLEdBQUcsY0FBYyxDQUFDLFVBQVUsQ0FBQyxDQUFBO0lBRTlDLE1BQU0sU0FBUyxHQUFHO1FBQ2hCOztXQUVHO1FBQ0gsRUFBRSxFQUFFLFNBQVM7UUFDYjs7V0FFRztRQUNILEtBQUssRUFBRSxXQUFXLENBQUMsS0FBSztRQUN4Qjs7V0FFRztRQUNILEVBQUUsRUFBRSxTQUFTO1FBQ2I7O1dBRUc7UUFDSCxFQUFFLEVBQUUsU0FBUztRQUNiOztXQUVHO1FBQ0gsRUFBRSxFQUFFLFNBQVM7UUFDYjs7V0FFRztRQUNILEVBQUUsRUFBRSxTQUFTO0tBQ0wsQ0FBQTtJQUVWLE9BQU8sU0FBUyxDQUFBO0FBQ2xCLENBQUMsQ0FBQTtBQUVEOztHQUVHO0FBQ0gsTUFBTSxDQUFDLE1BQU0sYUFBYSxHQUFHO0lBQzNCOztPQUVHO0lBQ0gsRUFBRSxFQUFFLHVCQUF1QjtJQUMzQjs7T0FFRztJQUNILEVBQUUsRUFBRSx3QkFBd0I7Q0FDcEIsQ0FBQTtBQUVWOztHQUVHO0FBQ0gsTUFBTSxDQUFDLE1BQU0sWUFBWSxHQUFHO0lBQzFCOztPQUVHO0lBQ0gsU0FBUyxFQUFFLHVCQUF1QjtJQUNsQzs7T0FFRztJQUNILFVBQVUsRUFBRSxTQUFTO0lBQ3JCOztPQUVHO0lBQ0gsUUFBUSxFQUFFLFNBQVM7Q0FDWCxDQUFBO0FBRVY7O0dBRUc7QUFDSCxNQUFNLENBQUMsTUFBTSxZQUFZLEdBT3JCO0lBQ0Y7O09BRUc7SUFDSCxPQUFPLEVBQUU7UUFDUDs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsT0FBTyxFQUFFLFNBQVM7UUFDbEI7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0Q7O09BRUc7SUFDSCxPQUFPLEVBQUU7UUFDUDs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsT0FBTyxFQUFFLFNBQVM7UUFDbEI7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0Q7O09BRUc7SUFDSCxPQUFPLEVBQUU7UUFDUDs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsT0FBTyxFQUFFLFNBQVM7UUFDbEI7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0Q7O09BRUc7SUFDSCxNQUFNLEVBQUU7UUFDTjs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsT0FBTyxFQUFFLFNBQVM7UUFDbEI7O1dBRUc7UUFDSCxZQUFZLEVBQUUsU0FBUztRQUN2Qjs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRDs7T0FFRztJQUNILE9BQU8sRUFBRTtRQUNQOztXQUVHO1FBQ0gsZUFBZSxFQUFFLFNBQVM7UUFDMUI7O1dBRUc7UUFDSCxPQUFPLEVBQUUsU0FBUztRQUNsQjs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLHVCQUF1QjtLQUM5QjtDQUNPLENBQUE7QUFFVjs7R0FFRztBQUNILE1BQU0sT0FBTyxHQUFHO0lBQ2QsRUFBRSxFQUFFO1FBQ0Y7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRCxFQUFFLEVBQUU7UUFDRjs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsZUFBZSxFQUFFLFNBQVM7UUFDMUI7O1dBRUc7UUFDSCxJQUFJLEVBQUUsU0FBUztLQUNoQjtJQUNELEVBQUUsRUFBRTtRQUNGOztXQUVHO1FBQ0gsVUFBVSxFQUFFLFNBQVM7UUFDckI7O1dBRUc7UUFDSCxlQUFlLEVBQUUsU0FBUztRQUMxQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0QsRUFBRSxFQUFFO1FBQ0Y7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRCxFQUFFLEVBQUU7UUFDRjs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsZUFBZSxFQUFFLFNBQVM7UUFDMUI7O1dBRUc7UUFDSCxJQUFJLEVBQUUsU0FBUztLQUNoQjtJQUNELEVBQUUsRUFBRTtRQUNGOztXQUVHO1FBQ0gsVUFBVSxFQUFFLFNBQVM7UUFDckI7O1dBRUc7UUFDSCxlQUFlLEVBQUUsU0FBUztRQUMxQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0QsRUFBRSxFQUFFO1FBQ0Y7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRCxFQUFFLEVBQUU7UUFDRjs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsZUFBZSxFQUFFLFNBQVM7UUFDMUI7O1dBRUc7UUFDSCxJQUFJLEVBQUUsU0FBUztLQUNoQjtJQUNELEVBQUUsRUFBRTtRQUNGOztXQUVHO1FBQ0gsVUFBVSxFQUFFLFNBQVM7UUFDckI7O1dBRUc7UUFDSCxlQUFlLEVBQUUsU0FBUztRQUMxQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0QsR0FBRyxFQUFFO1FBQ0g7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRCxHQUFHLEVBQUU7UUFDSDs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsZUFBZSxFQUFFLFNBQVM7UUFDMUI7O1dBRUc7UUFDSCxJQUFJLEVBQUUsU0FBUztLQUNoQjtJQUNELEdBQUcsRUFBRTtRQUNIOztXQUVHO1FBQ0gsVUFBVSxFQUFFLFNBQVM7UUFDckI7O1dBRUc7UUFDSCxlQUFlLEVBQUUsU0FBUztRQUMxQjs7V0FFRztRQUNILElBQUksRUFBRSxTQUFTO0tBQ2hCO0lBQ0QsR0FBRyxFQUFFO1FBQ0g7O1dBRUc7UUFDSCxVQUFVLEVBQUUsU0FBUztRQUNyQjs7V0FFRztRQUNILGVBQWUsRUFBRSxTQUFTO1FBQzFCOztXQUVHO1FBQ0gsSUFBSSxFQUFFLFNBQVM7S0FDaEI7SUFDRCxHQUFHLEVBQUU7UUFDSDs7V0FFRztRQUNILFVBQVUsRUFBRSxTQUFTO1FBQ3JCOztXQUVHO1FBQ0gsZUFBZSxFQUFFLFNBQVM7UUFDMUI7O1dBRUc7UUFDSCxJQUFJLEVBQUUsU0FBUztLQUNoQjtDQUNPLENBQUE7QUFFVjs7R0FFRztBQUNILE1BQU0sQ0FBQyxNQUFNLE9BQU8sR0FBMkI7SUFDN0MsT0FBTyxDQUFDLEVBQUU7SUFDVixPQUFPLENBQUMsRUFBRTtJQUNWLE9BQU8sQ0FBQyxFQUFFO0lBQ1YsT0FBTyxDQUFDLEVBQUU7SUFDVixPQUFPLENBQUMsRUFBRTtJQUNWLE9BQU8sQ0FBQyxFQUFFO0lBQ1YsT0FBTyxDQUFDLEVBQUU7SUFDVixPQUFPLENBQUMsRUFBRTtJQUNWLE9BQU8sQ0FBQyxFQUFFO0lBQ1YsT0FBTyxDQUFDLEdBQUc7SUFDWCxPQUFPLENBQUMsR0FBRztJQUNYLE9BQU8sQ0FBQyxHQUFHO0lBQ1gsT0FBTyxDQUFDLEdBQUc7SUFDWCxPQUFPLENBQUMsR0FBRztDQUNILENBQUE7QUFFVjs7Ozs7O0dBTUc7QUFDSCxNQUFNLENBQUMsTUFBTSxzQkFBc0IsR0FBRyxDQUFDLEtBQWEsRUFBRSxFQUFFLENBQ3RELE9BQU8sQ0FBQyxLQUFLLEdBQUcsT0FBTyxDQUFDLE1BQU0sQ0FBQyxDQUFBIn0=